// HELPERS (founder 2026-10-30: "skills and agents are many-to-many with DEs, so never force them
// into a tree"): Workforce › Skills (`/agents/skill-library?skill=`). Each distinct skill once,
// with "Used by" chips for every DE (its role's skills) and every helper (its `skills`) that has
// it; a skill that is one of the company's published skill packs links to that pack. Read from
// the cached lists the Workforce screens already hold (the agents, every page of the DEs, every
// page of the skill packs). A skill chip anywhere opens its entry here. SKILL-OVERRIDES: each
// entry opens in a modal (skill-overrides.tsx: our default, the company's additions, the
// differences, reset); `skills.list` adds its badges.
import {
  Button,
  EmptyState,
  LocationHeader,
  SearchField,
  Toolbar,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { EyeIcon, SparklesIcon } from "lucide-react";
import { type ComponentProps, useEffect, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { digitalEmployeesQuery, workforceAgentsQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { useEveryPage } from "../use-every-page";
import { LIST_BODY } from "./agents-ux";
import { type Helper, helperHref, useSkillPackIndex } from "./helpers";
import { SkillDialog, SkillStateBadges, skillStatesQuery } from "./skill-overrides";
import { skillPacksPaths } from "./skill-packs-screen";

type User =
  | { kind: "de"; id: string; name: string }
  | { kind: "ag"; id: string; name: string; helper: Helper };

export type SkillEntry = { key: string; users: User[] };

/** Every distinct skill, by key, with the DEs and helpers that use it (retired ones left out). */
export function skillLibrary(
  des: readonly {
    membership_id: string;
    display_name: string;
    status: string;
    role_skill_keys: readonly string[];
  }[],
  helpers: readonly Helper[],
  /**
   * SKILL-OVERRIDES: the DEs whose seat's packs include each skill (`skills.list` `mounted_by`:
   * a role's skill-pack assignment), so those skills are listed too.
   */
  mounted: ReadonlyMap<string, readonly string[]> = new Map(),
): SkillEntry[] {
  const by = new Map<string, User[]>();
  const add = (key: string, u: User) => {
    const k = key.trim();
    if (!k) return;
    const list = by.get(k) ?? [];
    if (!list.some((x) => x.kind === u.kind && x.id === u.id)) list.push(u);
    by.set(k, list);
  };
  const deBy = new Map(des.map((d) => [d.membership_id, d]));
  for (const d of des) {
    if (d.status === "retired") continue;
    for (const k of d.role_skill_keys)
      add(k, { kind: "de", id: d.membership_id, name: d.display_name });
  }
  for (const [k, ids] of mounted)
    for (const id of ids) {
      const d = deBy.get(id);
      if (d && d.status !== "retired")
        add(k, { kind: "de", id: d.membership_id, name: d.display_name });
    }
  for (const a of helpers) {
    if (a.status === "retired") continue;
    for (const k of a.skills ?? []) add(k, { kind: "ag", id: a.id, name: a.name, helper: a });
  }
  return [...by.entries()]
    .map(([key, users]) => ({
      key,
      users: users.sort((x, y) =>
        x.kind === y.kind ? x.name.localeCompare(y.name) : x.kind === "de" ? -1 : 1,
      ),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

export function SkillLibraryScreen({
  api,
  company,
  skill,
}: {
  api: Api;
  company: CompanyAccess;
  /** The entry to scroll to and mark (a skill chip's link). */
  skill?: string | undefined;
}) {
  const agents = useQuery(workforceAgentsQuery(api, company.id));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const allDes = useEveryPage(des);
  const packs = useSkillPackIndex(api, company.id, true);
  const [search, setSearch] = useState("");
  // SKILL-OVERRIDES: each skill's state (customized, a new default to review, self-hosted) and
  // the skill open in the modal.
  const states = useQuery({ ...skillStatesQuery(api, company.id), retry: false });
  const stateBy = useMemo(
    () => new Map((states.data?.data ?? []).map((s) => [s.key, s])),
    [states.data],
  );
  const [open, setOpen] = useState<string | null>(null);
  const entries = useMemo(
    () =>
      skillLibrary(
        des.data?.pages.flatMap((p) => p.data) ?? [],
        agents.data ?? [],
        new Map((states.data?.data ?? []).map((s) => [s.key, s.mounted_by])),
      ),
    [des.data, agents.data, states.data],
  );
  const q = search.trim().toLowerCase();
  const shown = q
    ? entries.filter(
        (e) =>
          e.key.toLowerCase().includes(q) || e.users.some((u) => u.name.toLowerCase().includes(q)),
      )
    : entries;
  const ready = agents.isSuccess && allDes.complete;
  useEffect(() => {
    if (!skill || !ready) return;
    document
      .querySelector(`[data-skill-entry="${CSS.escape(skill)}"]`)
      ?.scrollIntoView?.({ block: "center" });
  }, [skill, ready]);
  const paths = skillPacksPaths(company);
  return (
    <div className={LIST_BODY} data-slot="skill-library">
      <LocationHeader
        icon={SparklesIcon}
        title="Skills"
        {...(ready ? { count: entries.length } : {})}
      />
      <Toolbar bleed aria-label="Skills tools">
        <SearchField
          value={search}
          placeholder="Search skills…"
          aria-label="Search skills"
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>
      {agents.isError || allDes.failed ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="Skills" error={agents.error ?? des.error} />
        </div>
      ) : !ready ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading skills" rows={3} />
        </div>
      ) : !entries.length ? (
        <EmptyState
          variant="compact"
          icon={SparklesIcon}
          title="No skills yet"
          description="Skills show here once a DE's role or a helper has some."
          className="m-6"
        />
      ) : (
        <ul className="m-0 list-none p-0" data-slot="skill-entries">
          {shown.map((e) => {
            const pack = packs.get(e.key);
            return (
              <li
                key={e.key}
                data-skill-entry={e.key}
                data-focused={skill === e.key ? "true" : undefined}
                className={`group/skill flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-subtle px-6 py-2 max-md:px-4 ${skill === e.key ? "bg-primary-soft/60" : ""}`}
              >
                <button
                  type="button"
                  className="min-w-40 rounded-sm text-left font-mono text-sm text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  onClick={() => setOpen(e.key)}
                  data-slot="skill-open"
                >
                  {e.key}
                </button>
                {/* SKILL-OVERRIDES: open it (default, company additions, differences, reset) */}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Open ${e.key}`}
                  title="Open"
                  onClick={() => setOpen(e.key)}
                  className="-ml-2 text-faint opacity-0 group-hover/skill:opacity-100 hover:text-foreground focus-visible:opacity-100 pointer-coarse:opacity-100"
                >
                  <EyeIcon aria-hidden="true" />
                </Button>
                {states.isSuccess ? <SkillStateBadges state={stateBy.get(e.key)} /> : null}
                {pack ? (
                  <Link
                    to={paths.pack}
                    params={{ slug: company.slug, id: pack.id }}
                    search={keepFocus}
                    className="text-xs text-primary-text underline-offset-4 hover:underline"
                  >
                    Skill pack {pack.version}
                  </Link>
                ) : null}
                <span
                  className="flex min-w-0 flex-1 flex-wrap items-center gap-1"
                  data-slot="used-by"
                >
                  <span className="text-xs text-muted-foreground">Used by</span>
                  {e.users.map((u) => (
                    <Link
                      key={`${u.kind}:${u.id}`}
                      data-used-by={u.id}
                      {...((u.kind === "de"
                        ? {
                            to: "/c/$slug/agents/de/$id",
                            params: { slug: company.slug, id: u.id },
                            search: keepFocus,
                          }
                        : helperHref(company, u.helper)) as object as ComponentProps<typeof Link>)}
                      className="inline-flex items-center gap-1 rounded-full border border-subtle py-px pr-2 pl-1 text-xs text-muted-foreground hover:border-strong hover:text-foreground"
                    >
                      <WorkerBadge kind={u.kind} size="sm" />
                      {workerName(u.name)}
                    </Link>
                  ))}
                </span>
              </li>
            );
          })}
          {q && !shown.length ? (
            <li className="px-6 py-3 text-sm text-muted-foreground max-md:px-4">
              No skill matches "{search.trim()}".
            </li>
          ) : null}
        </ul>
      )}
      <SkillDialog api={api} company={company} skill={open} onClose={() => setOpen(null)} />
    </div>
  );
}
